/**
 * HamburgerMenu — Full-screen dialog with nav button grid + audio settings.
 * Opened via the ☰ button in the top bar or the M shortcut key.
 */

import Phaser from 'phaser';
import {
  Colors, CSSColors, Fonts, FontSize, Space, Depth,
  createPillButton, createDialogChrome, closeDialog,
} from './GameUITheme';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { AudioManager } from '../../managers/AudioManager';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

// Re-use the NAV_BUTTONS definition from MMOHUDScene via a callback pattern:
// The HUD scene passes in the button defs so we don't duplicate the list.
export interface HamburgerNavButton {
  label: string;
  icon?: string;
  iconKey?: string;
  colorKey?: string;
  width?: number;
  action: (scene: Phaser.Scene) => void;
}

export class HamburgerMenu {
  private container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, navButtons: HamburgerNavButton[]) {
    this.scene = scene;
    const { width, height } = scene.cameras.main;
    const mobile = isMobile(scene);

    const cols = mobile ? 2 : 3;
    const colW = mobile ? 160 : 126;
    const rowH = mobile ? 44 : 34;
    const btnH = mobile ? 38 : 28;
    const gap = mobile ? 8 : 6;
    const gridPadX = 12;

    const dialogW = mobile ? Math.min(width - 16, 360) : 420;
    // Calculate height based on button grid rows + audio + language sections
    const gridRows = Math.ceil(navButtons.length / cols);
    const gridH = gridRows * (rowH + gap);
    const audioSectionH = 120;
    const langSectionH = 80;
    const dialogH = Math.min(60 + gridH + 24 + audioSectionH + langSectionH + 24, height - 40);

    const cx = Math.round((width - dialogW) / 2);
    const cy = Math.round((height - dialogH) / 2);

    this.container = scene.add.container(cx, cy);
    this.container.setDepth(Depth.dialog);

    const close = () => this.close();

    createDialogChrome(scene, this.container, dialogW, dialogH, {
      onClose: close,
      title: I18n.t('dialog.menu'),
    });

    // ── Nav button grid ──
    const gridStartY = 58;

    navButtons.forEach((def, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const bx = gridPadX + col * (colW + gap);
      const by = gridStartY + row * (rowH + gap);

      const btn = createPillButton(scene, bx, by, def.label, {
        icon: def.icon,
        iconKey: def.iconKey,
        colorKey: def.colorKey,
        width: colW,
        height: btnH,
        depth: Depth.dialog + 1,
        onClick: () => {
          def.action(scene);
          close();
        },
      });
      this.container.add(btn);
    });

    // ── Audio Settings Section ──
    const audioY = gridStartY + gridRows * (rowH + gap) + (mobile ? 8 : 16);

    // Section divider
    const divider = scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.12);
    divider.lineBetween(Space.md, audioY, dialogW - Space.md, audioY);
    this.container.add(divider);

    const audioLabel = scene.add.text(Space.lg, audioY + 10, I18n.t('hamburger.audio'), {
      fontSize: FontSize.sm,
      color: CSSColors.cyanDim,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    });
    this.container.add(audioLabel);

    const audioManager = ServiceLocator.get<AudioManager>(Services.AUDIO);

    this.createSlider(dialogW, audioY + 36, I18n.t('hamburger.master'),
      audioManager.getMasterVolume(),
      (v) => audioManager.setMasterVolume(v));

    this.createSlider(dialogW, audioY + 72, I18n.t('hamburger.music'),
      audioManager.getMusicVolume(),
      (v) => audioManager.setMusicVolume(v));

    // ── Language Section ──
    const langY = audioY + 108;

    const langDivider = scene.add.graphics();
    langDivider.lineStyle(1, Colors.cyan, 0.12);
    langDivider.lineBetween(Space.md, langY, dialogW - Space.md, langY);
    this.container.add(langDivider);

    const langLabel = scene.add.text(Space.lg, langY + 10, I18n.t('options.language'), {
      fontSize: FontSize.sm,
      color: CSSColors.cyanDim,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
    });
    this.container.add(langLabel);

    const locales = I18n.getAvailableLocales();
    const currentLocale = I18n.getLocale();
    const langBtnW = 72;
    const langBtnHt = 24;
    const btnGap = 6;
    const btnStartX = Space.lg;
    const btnStartY = langY + 36;

    locales.forEach((loc, i) => {
      const bx = btnStartX + i * (langBtnW + btnGap);
      const isActive = loc.code === currentLocale;

      const bg = scene.add.graphics();
      bg.fillStyle(isActive ? Colors.cyan : 0x2a2a3a, isActive ? 0.3 : 1);
      bg.fillRoundedRect(bx, btnStartY, langBtnW, langBtnHt, 4);
      if (isActive) {
        bg.lineStyle(1, Colors.cyan, 0.6);
        bg.strokeRoundedRect(bx, btnStartY, langBtnW, langBtnHt, 4);
      }
      this.container.add(bg);

      const label = scene.add.text(bx + langBtnW / 2, btnStartY + langBtnHt / 2, loc.name, {
        fontSize: '11px',
        color: isActive ? CSSColors.textWhite : CSSColors.textLight,
        fontFamily: Fonts.body,
      }).setOrigin(0.5);
      this.container.add(label);

      const hitZone = scene.add.zone(bx + langBtnW / 2, btnStartY + langBtnHt / 2, langBtnW, langBtnHt)
        .setInteractive({ useHandCursor: true });
      this.container.add(hitZone);

      hitZone.on('pointerdown', () => {
        if (loc.code !== I18n.getLocale()) {
          I18n.setLocale(loc.code);
          close();
          scene.scene.start(scene.scene.key);
        }
      });
    });
  }

  private createSlider(
    dialogW: number, y: number, label: string,
    initialValue: number, onChange: (v: number) => void,
  ): void {
    const scene = this.scene;
    const labelX = Space.lg;
    const trackX = 100;
    const trackW = dialogW - trackX - Space.lg - 50;
    const trackH = 6;

    const labelText = scene.add.text(labelX, y + 2, label, {
      fontSize: FontSize.xs,
      color: CSSColors.textLight,
      fontFamily: Fonts.body,
    });
    this.container.add(labelText);

    // Track background
    const track = scene.add.graphics();
    track.fillStyle(0x2a2a3a, 1);
    track.fillRoundedRect(trackX, y + 4, trackW, trackH, 3);
    this.container.add(track);

    // Fill bar
    const fill = scene.add.graphics();
    const drawFill = (v: number) => {
      fill.clear();
      if (v > 0.005) {
        fill.fillStyle(Colors.cyan, 0.7);
        fill.fillRoundedRect(trackX, y + 4, trackW * v, trackH, 3);
      }
    };
    drawFill(initialValue);
    this.container.add(fill);

    // Value text
    const valText = scene.add.text(trackX + trackW + 8, y + 2, `${Math.round(initialValue * 100)}%`, {
      fontSize: FontSize.xs,
      color: CSSColors.textWhite,
      fontFamily: Fonts.body,
    });
    this.container.add(valText);

    // Clickable hit zone
    const hitZone = scene.add.zone(trackX + trackW / 2, y + 4 + trackH / 2, trackW, 20)
      .setInteractive({ useHandCursor: true });
    this.container.add(hitZone);

    hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Convert pointer world position to container-local
      const localX = pointer.x - this.container.x;
      const ratio = Phaser.Math.Clamp((localX - trackX) / trackW, 0, 1);
      onChange(ratio);
      drawFill(ratio);
      valText.setText(`${Math.round(ratio * 100)}%`);
    });
  }

  private close(): void {
    closeDialog(this.scene, this.container);
  }
}
